import { PrePurchase } from "./pre-purchase.interface";
import { PDFGenerator } from "./pdf-generator";
import type { jsPDF } from "jspdf";

export class PrePurchaseListGenerator {
  private readonly pdf: PDFGenerator;
  private readonly doc: jsPDF;

  private readonly title = "Link Pharmacy";
  private readonly fontSize = 8;
  // private readonly lineHeight = 5;
  private readonly margin = 4;
  private readonly titleFontSize = 14;
  // private readonly subTitleFontSize = 8;
  private readonly fontFamily = "Helvetica";

  private readonly pageWidth = 70;

  private readonly TEXT_HEIGHT_MULTIPLIER = 0.5;

  private heightUsed = 0;

  constructor(private prePurchase: PrePurchase) {
    const docHeight = this.calculateDocumentHeight();

    this.pdf = new PDFGenerator({ height: docHeight < 100 ? 100 : docHeight });
    this.doc = this.pdf.getDoc();

    this.pdf.setup();

    this.generate();
  }

  private generate() {
    this.setTitle();

    this.addSupplierName();
    this.addBrandName();
    this.addItems();
  }

  private calculateDocumentHeight() {
    //   consider title, supplier name, brand name, items table
    let height = 0;

    // Title
    height += this.getRequiredHeight(
      this.TEXT_HEIGHT_MULTIPLIER * 2,
      this.titleFontSize,
    );

    // Supplier Name
    if (this.prePurchase.supplierName) {
      height += this.getRequiredHeight(
        this.TEXT_HEIGHT_MULTIPLIER,
        this.fontSize,
      );
    }

    // Brand Name
    if (this.prePurchase.brandName) {
      height += this.getRequiredHeight(
        this.TEXT_HEIGHT_MULTIPLIER,
        this.fontSize,
      );
    }

    // Items Table
    const itemsCount = this.prePurchase.items.length;
    height += this.getRequiredHeight(
      this.TEXT_HEIGHT_MULTIPLIER,
      this.fontSize,
    ); // Table Header
    height +=
      itemsCount *
      this.getRequiredHeight(this.TEXT_HEIGHT_MULTIPLIER, this.fontSize); // Table Rows

    // Add some extra space
    height += this.getRequiredHeight(
      this.TEXT_HEIGHT_MULTIPLIER,
      this.fontSize,
    );

    return Math.ceil(height);
  }

  private addItems() {
    const heightPoint = this.TEXT_HEIGHT_MULTIPLIER;

    const tableData = this.prePurchase.items.map((item, _i) => [
      item.productName,
      item.quantity.toString(),
      item.retailPrice.toFixed(2),
    ]);

    (this.doc as any).autoTable({
      startY: this.calculateRequiredHeight(heightPoint),
      head: [[`Items (${this.prePurchase.items.length})`, "Quantity", "MRP"]],
      body: tableData,
      theme: "plain",

      styles: {
        font: this.fontFamily,
        fontSize: this.fontSize,

        lineWidth: 0,
        overflow: "linebreak",
        cellPadding: 1,
        fontStyle: "normal",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        lineColor: [100, 100, 100],
        textColor: [0, 0, 0],
        fontSize: this.fontSize,
        fontStyle: "bold",
        halign: "right",
        valign: "middle",
        // We'll custom draw borders so keep lineWidth 0 here
        lineWidth: 0,
      },
      margin: { left: this.margin },
      tableWidth: this.pageWidth - 2 * this.margin,

      columnStyles: {
        0: {
          cellWidth: this.pdf.calculateTableWidth(50),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: 0,
            right: 0,
          },
          overflow: "linebreak",
          halign: "left",
        }, // Item
        1: {
          cellWidth: this.pdf.calculateTableWidth(25),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: 0,
            right: 0,
          },
          halign: "right",
        }, // Price
        2: {
          cellWidth: this.pdf.calculateTableWidth(20),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: 0,
            right: 0,
          },
          halign: "right",
        }, // Qty
        // 3: {
        //   cellWidth: this.pdf.calculateTableWidth(20),
        //   cellPadding: {
        //     top: this.fontSize * 0.1,
        //     bottom: this.fontSize * 0.1,
        //     left: 0,
        //     right: 0,
        //   },
        //   halign: "right",
        // }, // Total Price
      },
      didDrawCell: (data: any) => {
        if (data.section === "head") {
          const { cell, doc } = data;
          const { x, y, width, height } = cell;
          // Draw a rectangle border around each header cell
          doc.setLineWidth(0.1);
          doc.rect(x, y, width, height);
        }
      },
      didParseCell: (data: any) => {
        // Make first header cell left-aligned
        if (data.section === "head" && data.column.index === 0) {
          data.cell.styles.halign = "left";
        }
      },
    });

    this.heightUsed = (this.doc as any).lastAutoTable.finalY;
  }

  private setTitle() {
    this.doc.setFontSize(this.titleFontSize);

    const heightPoint = this.TEXT_HEIGHT_MULTIPLIER * 2;

    this.doc.text(
      this.title,
      this.doc.internal.pageSize.width / 2,
      this.calculateRequiredHeight(heightPoint),
      {
        align: "center",
      },
    );

    this.addHeightUsed(heightPoint);
  }

  private addSupplierName() {
    if (!this.prePurchase.supplierName) {
      return;
    }

    this.doc.setFontSize(this.fontSize);

    const heightPoint = this.TEXT_HEIGHT_MULTIPLIER;

    const supplierText = `Supplier: ${this.prePurchase.supplierName}`;

    this.doc.text(
      supplierText,
      this.margin,
      this.calculateRequiredHeight(heightPoint),
      {
        align: "left",
      },
    );

    this.addHeightUsed(heightPoint);
  }

  private addBrandName() {
    if (!this.prePurchase.brandName) {
      return;
    }

    this.doc.setFontSize(this.fontSize);

    const heightPoint = this.TEXT_HEIGHT_MULTIPLIER;

    const brandText = `Brand: ${this.prePurchase.brandName}`;

    this.doc.text(
      brandText,
      this.margin,
      this.calculateRequiredHeight(heightPoint),
      {
        align: "left",
      },
    );

    this.addHeightUsed(heightPoint);
  }

  private calculateRequiredHeight(height: number) {
    const requiredHeight = this.getRequiredHeight(height);

    return this.getHeightUsed() + requiredHeight;
  }

  private getRequiredHeight(heightPoint: number, fontSize?: number) {
    const fsz = fontSize || this.fontSize;

    const multiplier = 1;

    return fsz * multiplier * heightPoint;
  }

  private getHeightUsed() {
    return this.heightUsed;
  }

  private addHeightUsed(height: number) {
    const requiredHeight = this.getRequiredHeight(height);
    this.heightUsed += requiredHeight;
  }

  public download() {
    let fileName = `pre-purchase`;

    if (this.prePurchase.supplierName) {
      fileName += `-${this.prePurchase.supplierName.replace(/\s+/g, "_").toLowerCase()}`;
    }

    fileName += `.pdf`;

    this.pdf.download(fileName);
  }

  public print() {
    this.pdf.print();
  }
}
